import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { firestore } from './firebase'; // Import firestore from firebase.js
import { addDoc, collection } from 'firebase/firestore';
import { FaStar, FaRegStar } from 'react-icons/fa'; // Import star icons
import { useNavigate } from 'react-router-dom'; // Import useNavigate from react-router-dom

const normalizeNameForLookup = (name) => (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
const buildChildLookupName = (firstName, lastName) =>
  `${normalizeNameForLookup(firstName)}|${normalizeNameForLookup(lastName)}`;

const AddNewStudentForm = () => {
  const [parentHasChildEnrolled] = useState(false);
  const [numChildren, setNumChildren] = useState(1); // Default to 1 child
  const [selectedDateOfEnquiry, setSelectedDateOfEnquiry] = useState(null);
  const [selectedDateOfEnrollment, setSelectedDateOfEnrollment] = useState(null);
  const [status, setStatus] = useState('Called in'); // Default status
  const [childrenDOB, setChildrenDOB] = useState([null]); // State for children date of birth
  const [isPriority, setIsPriority] = useState(false); // State for priority
  const [preferredContactMethod, setPreferredContactMethod] = useState('Email'); // Default to Email
  const [whichWaitlist, setWhichWaitlist] = useState('Main'); // New state for waitlist selection
  const navigate = useNavigate(); // Initialize useNavigate

  const handleNumChildrenChange = (e) => {
    const childrenCount = parseInt(e.target.value);
    setNumChildren(childrenCount);
    setChildrenDOB(new Array(childrenCount).fill(null)); // Update the DOB state array based on number of children
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  const handleChildDOBChange = (index, date) => {
    const newChildrenDOB = [...childrenDOB];
    newChildrenDOB[index] = date;
    setChildrenDOB(newChildrenDOB);
  };

  const handlePreferredContactMethodChange = (e) => {
    setPreferredContactMethod(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const siblings = parentHasChildEnrolled ? "Yes" : "No";

    const parentData = {
      parentFirstName: e.target.elements.parentFirstName.value,
      parentLastName: e.target.elements.parentLastName.value,
      email: e.target.elements.email.value,
      phoneNumber: e.target.elements.phoneNumber.value,
      contactMethod: e.target.elements.contactMethod.value,
    };

    const enquiryData = {
      enquiryDate: selectedDateOfEnquiry,
      desiredEnrollmentDate: selectedDateOfEnrollment,
      status: status,
      priority: isPriority,
      whichWaitlist: whichWaitlist, // Save selected waitlist value
    };

    // Validation checks
    if (!selectedDateOfEnquiry || !status || !parentData.parentFirstName || !parentData.parentLastName) {
      alert("Please fill out all required fields: Enquiry Date, Status, Parent First and Last Name");
      return;
    }

    const childrenData = [];

    for (let i = 0; i < numChildren; i++) {
      const childFirstName = e.target.elements[`childFirstName${i}`].value;
      const childLastName = e.target.elements[`childLastName${i}`].value;
      const dateOfBirth = childrenDOB[i];
      const allergies = e.target.elements[`allergies${i}`].value;
      const notesInput = e.target.elements[`notes${i}`].value.trim(); // Trim to check for empty

      if (!childFirstName || !childLastName || !dateOfBirth) {
        alert("Please fill out all required fields for each child: First Name, Last Name, Date of Birth.");
        return;
      }

      const age = calculateAge(dateOfBirth);
      const childClass = getClassBasedOnAge(age);

      let notes = [];

      if (notesInput !== "") {
        notes = notesInput.split('\n').map(note => ({
          content: note,
          timestamp: new Date().toISOString(),
        }));
      }

      const childData = {
        firstName: childFirstName,
        lastName: childLastName,
        dateOfBirth: dateOfBirth,
        allergies: allergies,
        notes: notes, // Include notes array with or without timestamp
        hasSiblingEnrolled: siblings,
        class: childClass,
        siblings: [], // Initialize empty siblings array
      };

      childrenData.push(childData);
    }

    // Track siblings
    childrenData.forEach((child, index) => {
      const siblingNames = childrenData
        .filter((_, siblingIndex) => siblingIndex !== index)
        .map(sibling => `${sibling.firstName} ${sibling.lastName}`);
      child.siblings = siblingNames;
    });

    // Add each child as a separate document
    try {
      for (let childData of childrenData) {
        const formData = {
          enquiry: enquiryData,
          parent: parentData,
          children: [childData],
          childLookupName: buildChildLookupName(childData.firstName, childData.lastName),
        };

        const docRef = await addDoc(collection(firestore, 'students'), formData);
        console.log('Document written with ID: ', docRef.id);
      }

      navigate('/waitlist'); // Navigate back to the waitlist
    } catch (error) {
      console.error('Error adding document: ', error);
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
    }

    return age + (monthDiff / 12) + (dayDiff / 365.25);
  };

  const getClassBasedOnAge = (age) => {
    switch (true) {
        case (age >= 0 && age < 1): // 8 weeks to <12 months
            return "Cubs";
        case (age >= 1 && age < 1.5): // 12 to <18 months
            return "Teddies";
        case (age >= 1.5 && age < 2): // 18 to <24 months
            return "Koalas";
        case (age >= 2 && age < 3): // 2 to <3 years
            return "Pandas";
        case (age >= 3 && age < 4): // 3 to <4 years
            return "Preschool";
        case (age >= 4 && age < 5): // 4 to <5 years
            return "Pre-K";
        case (age >= 5 && age < 6): // 5 years and older
            return "Kindergarten";
        default:
            return "Unknown"; // Added a default case for safety
    }
  };

  const handleChildFirstNameChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, '');
  };

  return (
    <div className="max-w-4xl mx-auto p-10 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-center">Add New Student</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Enquiry Info */}
        <div>
          <label className="block text-lg font-medium text-gray-700 p-2">Enquiry Date</label>
          <DatePicker
            selected={selectedDateOfEnquiry}
            onChange={(date) => setSelectedDateOfEnquiry(date)}
            maxDate={new Date()}
            className="border border-gray-300 rounded-md p-2"
          />
        </div>
        <div>
          <label className="block text-lg font-medium text-gray-700 p-2">Desired Enrollment Date</label>
          <DatePicker
            selected={selectedDateOfEnrollment}
            onChange={(date) => setSelectedDateOfEnrollment(date)}
            showYearDropdown
            scrollableMonthYearDropdown
            className="border border-gray-300 rounded-md p-2"
          />
        </div>
        <div>
          <label className="block text-lg font-medium text-gray-700 p-2">Status</label>
          <select
            value={status}
            onChange={handleStatusChange}
            className="block w-full border border-gray-300 rounded-md p-2"
          >
            <option value="Called in">Called in</option>
            <option value="Enquired">Enquired</option>
            <option value="Emailed">Emailed</option>
            <option value="Toured">Toured</option>
          </select>
        </div>
        
        {/* Waitlist Picker */}
        <div>
          <label className="block text-lg font-medium text-gray-700 p-2">Select Waitlist</label>
          <select
            value={whichWaitlist}
            onChange={(e) => setWhichWaitlist(e.target.value)}
            className="block w-full border border-gray-300 rounded-md p-2"
          >
            <option value="Main">Main</option>
            <option value="Starting">Starting</option>
            <option value="Future">Future / Incoming</option>
            <option value="Declined">Declined</option>
            <option value="Archived">Archived</option>
            <option value="Enrolled">Enrolled</option>
            <option value="Too Old">Too Old</option>
          </select>
        </div>

        {/* Priority */}
        <div className="flex items-center space-x-2">
          <label className="text-lg font-medium text-gray-700">Priority</label>
          <div onClick={() => setIsPriority(!isPriority)} className="cursor-pointer">
            {isPriority ? <FaStar color="gold" size={24} /> : <FaRegStar color="gray" size={24} />}
          </div>
        </div>

        {/* Parent Info */}
        <div>
          <h3 className="text-lg font-bold mb-2">Parent Info</h3>
          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-lg font-medium text-gray-700 p-2">Parent First Name</label>
              <input type="text" name="parentFirstName" className="border border-gray-300 rounded-md p-2 w-full" />
            </div>
            <div className="w-1/2">
              <label className="block text-lg font-medium text-gray-700 p-2">Parent Last Name</label>
              <input type="text" name="parentLastName" className="border border-gray-300 rounded-md p-2 w-full" />
            </div>
          </div>
          <div className="flex space-x-4">
            <div className="w-1/2">
              <label className="block text-lg font-medium text-gray-700 p-2">Email (Optional)</label>
              <input type="email" name="email" className="border border-gray-300 rounded-md p-2 w-full" />
            </div>
            <div className="w-1/2">
              <label className="block text-lg font-medium text-gray-700 p-2">Phone Number (Optional)</label>
              <input type="tel" name="phoneNumber" className="border border-gray-300 rounded-md p-2 w-full" />
            </div>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 p-2">Select Preferred Contact Method</label>
            <select name="contactMethod" value={preferredContactMethod} onChange={handlePreferredContactMethodChange} className="block w-full border border-gray-300 rounded-md p-2">
              <option value="Email">Email</option>
              <option value="Phone">Phone</option>
            </select>
          </div>
        </div>

        {/* Child Info */}
        <div>
          <h3 className="text-lg font-bold mb-2">Child Info</h3>
          <label className="block text-lg font-medium text-gray-700 p-2">How many children is this parent looking to enroll?</label>
          <input
            type="number"
            value={numChildren}
            onChange={handleNumChildrenChange}
            min="1"
            max="10"
            className="border border-gray-300 rounded-md p-2 w-full"
          />
        </div>

        {/* Render fields for each child */}
        {[...Array(numChildren)].map((_, index) => (
          <div className="border border-gray-300 rounded-md p-4 space-y-4" key={index}>
            <h4 className="text-lg font-bold mb-2">Child {index + 1}</h4>
            <div className="flex space-x-4">
              <div className="w-1/2">
                <label className="block text-lg font-medium text-gray-700">Child First Name</label>
                <input
                  type="text"
                  name={`childFirstName${index}`}
                  className="border border-gray-300 rounded-md p-2 w-full"
                  onChange={handleChildFirstNameChange}
                />
              </div>
              <div className="w-1/2">
                <label className="block text-lg font-medium text-gray-700">Child Last Name</label>
                <input
                  type="text"
                  name={`childLastName${index}`}
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>
            </div>
            <div className="flex space-x-4">
              <div className="w-1/2">
                <label className="block text-lg font-medium text-gray-700">Child's Date of Birth</label>
                <DatePicker
                  selected={childrenDOB[index]}
                  onChange={(date) => handleChildDOBChange(index, date)}
                  showYearDropdown
                  popperPlacement="top"
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>
              <div className="w-1/2">
                <label className="block text-lg font-medium text-gray-700">Allergies</label>
                <textarea
                  rows="3"
                  name={`allergies${index}`}
                  className="border border-gray-300 rounded-md p-2 w-full"
                ></textarea>
              </div>
            </div>
            <div>
              <label className="block text-lg font-medium text-gray-700">Notes</label>
              <textarea
                rows="3"
                name={`notes${index}`}
                className="border border-gray-300 rounded-md p-2 w-full"
              ></textarea>
            </div>
          </div>
        ))}

        {/* Submit button */}
        <div className="text-center">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow-lg"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddNewStudentForm;
